/**
 * Markdown setzen — so viel, wie ein Analysebericht braucht, und nicht mehr.
 *
 * Bewusst ohne fremde Bibliothek und ohne `dangerouslySetInnerHTML`: die
 * Analyse kommt aus der Datenbank, und was aus der Datenbank kommt, wird nicht
 * als HTML in die Seite gelassen. Hier entstehen React Elemente, kein Markup —
 * damit ist die ganze Klasse von Einschleusungsfehlern nicht vorhanden statt
 * abgefangen.
 *
 * Unterstuetzt: Ueberschriften, Absaetze, Listen, Tabellen, Zitatbloecke,
 * Codebloecke, Trennlinien, fett, kursiv und Code im Fliesstext.
 */
import type { ReactNode } from 'react'

/* ────────────────────────────────────────────────── Zeichen im Fliesstext */

/** `**fett**`, `*kursiv*` und `` `code` `` — mehr kommt im Bericht nicht vor. */
function zeichen(text: string, schluessel: string): ReactNode[] {
  const teile: ReactNode[] = []
  const muster = /(\*\*[^*]+\*\*|`[^`]+`|\*[^*\n]+\*)/g
  let zuletzt = 0
  let treffer: RegExpExecArray | null
  let n = 0

  while ((treffer = muster.exec(text)) !== null) {
    if (treffer.index > zuletzt) teile.push(text.slice(zuletzt, treffer.index))
    const roh = treffer[0]
    const k = `${schluessel}-${n++}`
    if (roh.startsWith('**')) teile.push(<strong key={k}>{roh.slice(2, -2)}</strong>)
    else if (roh.startsWith('`')) teile.push(<code key={k}>{roh.slice(1, -1)}</code>)
    else teile.push(<em key={k}>{roh.slice(1, -1)}</em>)
    zuletzt = treffer.index + roh.length
  }
  if (zuletzt < text.length) teile.push(text.slice(zuletzt))
  return teile
}

/* ─────────────────────────────────────────────────────────────── Tabellen */

function istTrennzeile(zeile: string): boolean {
  return /^\s*\|?[\s:|-]+\|[\s:|-]*$/.test(zeile) && zeile.includes('-')
}

function spalten(zeile: string): string[] {
  return zeile
    .replace(/^\s*\|/, '')
    .replace(/\|\s*$/, '')
    .split('|')
    .map((s) => s.trim())
}

/**
 * Rechtsbuendig, wenn der Inhalt eine Zahl ist. Beträge müssen in einer Spalte
 * untereinander stehen, sonst liest man sie nicht.
 */
function istZahl(wert: string): boolean {
  return /^[−–-]?[\d.]+(,\d+)?\s*(%|€|EUR|Tsd|Mio|pp|Jahre)?$/.test(wert.trim()) && /\d/.test(wert)
}

/* ─────────────────────────────────────────────────────────────── Aufbau */

export function Markdown({ text, gliedern = false }: { text: string; gliedern?: boolean }): ReactNode {
  const zeilen = text.replace(/\r\n/g, '\n').split('\n')
  const raus: ReactNode[] = []
  /* Welche Bloecke Ueberschriften sind, und welcher Stufe. */
  const stufen = new Map<number, number>()
  /* Tabellen und Zwischenueberschriften, fuer die Gliederung im Druck. */
  const arten = new Map<number, string>()
  let i = 0
  let n = 0
  const k = () => `md-${n++}`

  while (i < zeilen.length) {
    const zeile = zeilen[i] ?? ''

    // Leerzeile
    if (zeile.trim() === '') {
      i += 1
      continue
    }

    // Trennlinie
    if (/^\s*(---|___|\*\*\*)\s*$/.test(zeile)) {
      raus.push(<hr key={k()} />)
      i += 1
      continue
    }

    // Codeblock
    if (zeile.trim().startsWith('```')) {
      const inhalt: string[] = []
      i += 1
      while (i < zeilen.length && !(zeilen[i] ?? '').trim().startsWith('```')) {
        inhalt.push(zeilen[i] ?? '')
        i += 1
      }
      i += 1
      raus.push(
        <pre key={k()}>
          <code>{inhalt.join('\n')}</code>
        </pre>,
      )
      continue
    }

    // Überschrift
    const ueberschrift = /^(#{1,6})\s+(.*)$/.exec(zeile)
    if (ueberschrift) {
      const stufe = Math.min(ueberschrift[1]!.length, 6)
      const Tag = `h${stufe}` as 'h1'
      stufen.set(raus.length, stufe)
      raus.push(<Tag key={k()}>{zeichen(ueberschrift[2]!, k())}</Tag>)
      i += 1
      continue
    }

    // Tabelle: Kopfzeile, Trennzeile, dann Daten
    if (zeile.includes('|') && istTrennzeile(zeilen[i + 1] ?? '')) {
      const kopf = spalten(zeile)
      i += 2
      const koerper: string[][] = []
      while (i < zeilen.length && (zeilen[i] ?? '').includes('|')) {
        koerper.push(spalten(zeilen[i] ?? ''))
        i += 1
      }
      /* Ausrichtung spaltenweise, nicht zellenweise. Sonst steht in einer
         Zahlenspalte eine Fehlanzeige wie "—" links und alles andere rechts,
         und der Spaltenkopf steht wieder woanders. Entschieden wird nach der
         Mehrheit der Zellen. */
      const spaltenzahl = Math.max(kopf.length, ...koerper.map((r) => r.length))
      const zahlenspalte: boolean[] = []
      for (let s = 0; s < spaltenzahl; s += 1) {
        const zellen = koerper.map((r) => r[s] ?? '').filter((z) => z !== '')
        zahlenspalte[s] = zellen.length > 0 && zellen.filter(istZahl).length * 2 > zellen.length
      }

      // Kurze Tabellen haelt der Druck zusammen, lange duerfen umbrechen.
      arten.set(raus.length, koerper.length > 15 ? 'tabelle' : 'tabelle-kurz')
      raus.push(
        <div className="md-tabelle" key={k()}>
          <table>
            <thead>
              <tr>
                {kopf.map((z, s) => (
                  <th key={s} className={zahlenspalte[s] ? 'zahl' : undefined}>
                    {zeichen(z, `${s}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {koerper.map((reihe, r) => (
                <tr key={r}>
                  {reihe.map((z, s) => (
                    <td key={s} className={zahlenspalte[s] ? 'zahl' : undefined}>
                      {zeichen(z, `${r}-${s}`)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      )
      continue
    }

    // Zitatblock
    if (/^\s*>\s?/.test(zeile)) {
      const inhalt: string[] = []
      while (i < zeilen.length && /^\s*>\s?/.test(zeilen[i] ?? '')) {
        inhalt.push((zeilen[i] ?? '').replace(/^\s*>\s?/, ''))
        i += 1
      }
      raus.push(
        <blockquote key={k()}>
          <Markdown text={inhalt.join('\n')} />
        </blockquote>,
      )
      continue
    }

    // Liste, mit Kästchen für die Prüflisten
    const listenBeginn = /^\s*([-*+]|\d+\.)\s+/.exec(zeile)
    if (listenBeginn) {
      const geordnet = /\d/.test(listenBeginn[1]!)
      const punkte: string[] = []
      while (i < zeilen.length) {
        const z = zeilen[i] ?? ''
        const treffer = /^\s*([-*+]|\d+\.)\s+(.*)$/.exec(z)
        if (treffer) {
          if (/\d/.test(treffer[1]!) !== geordnet) break
          punkte.push(treffer[2]!)
          i += 1
        } else if (/^\s{2,}\S/.test(z) && punkte.length > 0) {
          // Fortsetzungszeile eines Punktes
          punkte[punkte.length - 1] += ' ' + z.trim()
          i += 1
        } else break
      }
      const Tag = geordnet ? 'ol' : 'ul'
      raus.push(
        <Tag key={k()}>
          {punkte.map((p, nr) => {
            const kasten = /^\[([ xX])\]\s+/.exec(p)
            if (!kasten) return <li key={nr}>{zeichen(p, `${nr}`)}</li>
            return (
              <li key={nr} className="md-haken">
                <input type="checkbox" checked={kasten[1] !== ' '} readOnly aria-hidden />
                {zeichen(p.slice(kasten[0].length), `${nr}`)}
              </li>
            )
          })}
        </Tag>,
      )
      continue
    }

    // Absatz: bis zur nächsten Leerzeile oder zum nächsten Blockanfang
    const absatz: string[] = []
    while (i < zeilen.length) {
      const z = zeilen[i] ?? ''
      if (
        z.trim() === '' ||
        /^(#{1,6})\s/.test(z) ||
        /^\s*([-*+]|\d+\.)\s/.test(z) ||
        /^\s*>/.test(z) ||
        z.trim().startsWith('```') ||
        /^\s*(---|___|\*\*\*)\s*$/.test(z) ||
        (z.includes('|') && istTrennzeile(zeilen[i + 1] ?? ''))
      )
        break
      absatz.push(z.trim())
      i += 1
    }
    if (absatz.length > 0) {
      const inhalt = absatz.join(' ')
      /* Ein Absatz, der nur aus Fettem besteht ("Die Zahlen", "Handgriffe"),
         ist eine Zwischenueberschrift. Im Druck bleibt sie bei dem, was folgt. */
      const zwischentitel = /^\*\*[^*]+\*\*:?$/.test(inhalt)
      if (zwischentitel) arten.set(raus.length, 'zwischentitel')
      raus.push(
        <p key={k()} className={zwischentitel ? 'md-zwischentitel' : undefined}>
          {zeichen(inhalt, k())}
        </p>,
      )
    }
  }

  return <>{gliedern ? abschnitte(raus, stufen, arten) : raus}</>
}

/**
 * Fuer den Druck: Jede Ueberschrift und jede Zwischenueberschrift bildet mit
 * dem, was bis zur naechsten folgt, einen Teil (Kopf eines Anlasses mit
 * Befund und Gewicht, "Die Zahlen" mit der Liste, "Handgriffe" mit Fragen).
 * Der Druck haelt einen Teil zusammen, trennt aber zwischen den Teilen. So
 * steht nie eine Ueberschrift allein am Seitenende, und es bleibt trotzdem
 * keine halbe Seite leer. Teile mit Tabelle duerfen umbrechen, sonst springt
 * eine lange Tabelle ganz auf die naechste Seite. Kurze Tabellen (bis 15
 * Zeilen) zaehlen nicht als lang.
 */
function abschnitte(bloecke: ReactNode[], stufen: Map<number, number>, arten: Map<number, string>): ReactNode[] {
  const beginnt = (i: number) => stufen.has(i) || arten.get(i) === 'zwischentitel'
  const raus: ReactNode[] = []
  let i = 0
  while (i < bloecke.length) {
    if (!beginnt(i) || stufen.get(i) === 1) {
      raus.push(bloecke[i])
      i += 1
      continue
    }
    const anfang = i
    i += 1
    while (i < bloecke.length && !beginnt(i)) i += 1
    let mitTabelle = false
    for (let j = anfang; j < i; j += 1) if (arten.get(j) === 'tabelle') mitTabelle = true
    raus.push(
      <section className={mitTabelle ? 'md-teil md-teil-lang' : 'md-teil'} key={`teil-${anfang}`}>
        {bloecke.slice(anfang, i)}
      </section>,
    )
  }
  return raus
}
